// src/context/MusicContext.tsx - SIMPLIFIED VERSION
/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useState, useCallback, useMemo, ReactNode } from 'react';
import type { MusicContextType, LofiChannel } from '../types/index.ts';

export const MusicContext = createContext<MusicContextType | undefined>(undefined);

interface MusicProviderProps {
  children: ReactNode;
}

export const MusicProvider: React.FC<MusicProviderProps> = ({ children }) => {
  // State management
  const [currentChannel, setCurrentChannel] = useState<LofiChannel | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolumeState] = useState<number>(70); // Default 70%

  // Stable function references via useCallback
  const playChannel = useCallback((channel: LofiChannel): void => {
    setCurrentChannel(channel);
    setIsPlaying(true);
  }, []);

  const pauseMusic = useCallback((): void => {
    setIsPlaying(false);
  }, []);

  const resumeMusic = useCallback((): void => {
    setIsPlaying(true);
  }, []);

  const stopMusic = useCallback((): void => {
    setCurrentChannel(null);
    setIsPlaying(false);
  }, []);

  const setVolume = useCallback((newVolume: number): void => {
    setVolumeState(newVolume);
  }, []);

  const value = useMemo<MusicContextType>(() => ({
    isPlaying,
    currentChannel,
    volume,
    playChannel,
    pauseMusic,
    resumeMusic,
    stopMusic,
    setVolume,
  }), [isPlaying, currentChannel, volume, playChannel, pauseMusic, resumeMusic, stopMusic, setVolume]);

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
};
